<script setup>
import { AdyenCheckout, Card } from '@adyen/adyen-web';
import '@adyen/adyen-web/styles/adyen.css';
import './adyen-overrides.css';
import { inject, onMounted, onUnmounted, useTemplateRef } from 'vue';
import { dispatchCmprInputFocus } from '@cta/frontend/javascripts/piwik_web_analytics';
import CmprInput from '@cta/frontend/components/CmprInput.vue';

let adyenCard;

const props = defineProps({
  adyenSettings: {
    type: Object,
    default() {
      return {
        clientKey: undefined,
        environment: undefined,
        gatewayMerchantId: undefined,
        googlePayMerchantId: undefined,
        merchantName: undefined,
        mobilePaymentTypes: {
          type: Array,
          default: () => [],
        },
      };
    },
  },
});

const store = inject('store');
const container = useTemplateRef('container');

function globalConfiguration() {
  // From https://github.com/Adyen/adyen-web/blob/main/packages/server/translations/de-DE.json
  const translations = {
    'de-DE': {
      'cc.num.902': 'Bitte gib eine gültige Kartennummer ein',
      'cc.num.900': 'Bitte gib die Kartennummer ein',
      'cc.num.903': 'Bitte gib eine unterstützte Kartenmarke ein',
      'cc.num.901': 'Bitte gib die vollständige Kartennummer ein',
      'cc.dat.912': 'Bitte gib ein gültiges Ablaufdatum ein',
      'cc.dat.913': 'Bitte gib ein gültiges Ablaufdatum ein',
      'cc.dat.910': 'Bitte gib das Ablaufdatum ein',
      'cc.dat.911': 'Bitte gib das vollständige Ablaufdatum ein',
      'cc.mth.915': 'Bitte gib den Ablaufmonat ein',
      'cc.yr.917': 'Bitte gib das Ablaufjahr ein',
      'cc.yr.918': 'Bitte gib das vollständige Ablaufjahr ein',
      'cc.cvc.920': 'Bitte gib den Sicherheitscode ein',
      'cc.cvc.921': 'Bitte gib den vollständigen Sicherheitscode ein',
    },
  };

  return {
    environment: props.adyenSettings['environment'],
    amount: {
      value: store.values.amount,
      currency: 'EUR',
    },
    locale: 'de-DE',
    countryCode: 'DE',
    clientKey: props.adyenSettings['clientKey'],
    showPayButton: false,
    translations: translations,
    onChange: () => {
      store.paymentErrorUnlocked = false;

      if (adyenCard.data.paymentMethod.encryptedCardNumber && adyenCard.data.paymentMethod.encryptedExpiryMonth
        && adyenCard.data.paymentMethod.encryptedExpiryYear && adyenCard.data.paymentMethod.encryptedSecurityCode) {
        store.values.adyenData = adyenCard.data;
        store.errors.adyenData = undefined;
      } else {
        store.values.adyenData = undefined;
        store.errors.adyenData = 'Die Zahlungsdaten sind ungültig. Bitte überprüfe sie und versuche es erneut.';
      }
    },
  };
}

function cardConfiguration() {
  const element = getComputedStyle(document.querySelector('.cmpr-form-control'));

  // From https://docs.adyen.com/payment-methods/cards/custom-card-integration/#styling
  const styles = {
    base: {
      caretColor: element.getPropertyValue('--cmpr-color-neutral-70'),
      color: element.getPropertyValue('--cmpr-color-neutral-70'),
      fontFamily: element.getPropertyValue('font-family'),
      fontSize: element.getPropertyValue('font-size'),
      fontWeight: element.getPropertyValue('--cmpr-font-weight-10'),
      lineHeight: element.getPropertyValue('line-height'),
    },
    placeholder: {
      color: element.getPropertyValue('--cmpr-color-neutral-60'),
      fontFamily: element.getPropertyValue('font-family'),
      fontSize: element.getPropertyValue('font-size'),
      fontWeight: element.getPropertyValue('--cmpr-font-weight-10'),
    },
    error: {
      color: element.getPropertyValue('--cmpr-color-neutral-70'),
    },
  };

  return {
    onFocus: (event) => {
      const innerText = {
        encryptedCardNumber: 'number',
        encryptedExpiryDate: 'expiry-date',
        encryptedSecurityCode: 'security-code',
      }[event.fieldType];

      if (innerText) dispatchCmprInputFocus({ target: container.value, details: { context: 'payment-step', innerText } });
      else throw new Error(`Field type ${event.fieldType} not supported by web tracking.`);
    },
    placeholders: { cardNumber: 'Kartennummer', expiryDate: 'Ablaufdatum', securityCodeThreeDigits: 'Sicherheitscode' },
    styles: styles,
  };
}

async function initAdyen() {
  const adyenCheckout = await AdyenCheckout(globalConfiguration());
  adyenCard = new Card(adyenCheckout, cardConfiguration()).mount(container.value);
}

onMounted(() => {
  initAdyen();
});

onUnmounted(() => {
  store.values.adyenData = undefined;
  store.errors.adyenData = undefined;
});
</script>

<template>
  <CmprInput />

  <cmpr-alert-banner
    v-if="store.paymentErrorUnlocked && store.errors.adyenData"
    variant="error"
  >
    {{ store.errors.adyenData }}
  </cmpr-alert-banner>

  <div ref="container" />
</template>

<style scoped lang="scss">
  .cmpr-form-control {
    display: none;
  }
</style>
